import { assign, setup } from 'xstate';

import type { SchedulesEvent } from './events';

export type RouteId = 'E' | '44';
export type DirectionId = 'northbound' | 'southbound' | 'eastbound' | 'westbound';

function isDirectionId(value: string): value is DirectionId {
  return value === 'northbound' || value === 'southbound' || value === 'eastbound' || value === 'westbound';
}

export type Stop = { id: string; name: string; directionId: DirectionId };
export type Route = {
  id: RouteId;
  name: string;
  directions: Array<{ id: DirectionId; name: string; stops: Stop[] }>;
};
export type Arrival = { time: string; minutes: number; source: 'scheduled' | 'missing' };

export const routes: Route[] = [
  {
    id: 'E',
    name: 'RapidRide E Line',
    directions: [
      {
        id: 'northbound',
        name: 'Northbound · Aurora Village',
        stops: [
          { id: 'e-3rd-pike', name: '3rd Ave & Pike St', directionId: 'northbound' },
          { id: 'e-queen-royal', name: 'Queen Anne Ave N & Roy St', directionId: 'northbound' },
          { id: 'e-aurora-85', name: 'Aurora Ave N & N 85th St', directionId: 'northbound' },
        ],
      },
      {
        id: 'southbound',
        name: 'Southbound · Downtown Seattle',
        stops: [
          { id: 'e-aurora-85-s', name: 'Aurora Ave N & N 85th St', directionId: 'southbound' },
          { id: 'e-queen-royal-s', name: 'Queen Anne Ave N & Roy St', directionId: 'southbound' },
          { id: 'e-3rd-pike-s', name: '3rd Ave & Pike St', directionId: 'southbound' },
        ],
      },
    ],
  },
  {
    id: '44',
    name: 'Route 44',
    directions: [
      {
        id: 'eastbound',
        name: 'Eastbound · University District',
        stops: [
          { id: '44-ballard', name: 'Ballard Ave NW & Market St', directionId: 'eastbound' },
          { id: '44-fremont', name: 'Fremont Ave N & N 36th St', directionId: 'eastbound' },
          { id: '44-udistrict', name: 'NE 45th St & University Way NE', directionId: 'eastbound' },
        ],
      },
      {
        id: 'westbound',
        name: 'Westbound · Ballard',
        stops: [
          { id: '44-udistrict-w', name: 'NE 45th St & University Way NE', directionId: 'westbound' },
          { id: '44-fremont-w', name: 'Fremont Ave N & N 36th St', directionId: 'westbound' },
          { id: '44-ballard-w', name: 'Ballard Ave NW & Market St', directionId: 'westbound' },
        ],
      },
    ],
  },
];

type Timetable = Record<string, number[]>;
const timetable: Timetable = {
  'E|northbound|e-3rd-pike': [
    1450, 1490, 1530, 300, 330, 360, 390, 420, 450, 480, 510, 540, 570, 600, 630, 660, 690, 720, 750, 780, 810, 840,
    870, 900, 930, 960, 990, 1020, 1050, 1080, 1110, 1140, 1170, 1200, 1230, 1260, 1290, 1320, 1350, 1380,
  ],
  'E|northbound|e-queen-royal': [
    315, 345, 375, 405, 435, 465, 495, 525, 555, 585, 615, 645, 675, 705, 735, 765, 795, 825, 855, 885, 915, 945, 975,
    1005, 1035, 1065, 1095, 1125, 1155, 1185, 1215, 1245, 1275, 1305, 1335, 1365, 1395,
  ],
  'E|northbound|e-aurora-85': [
    330, 360, 390, 420, 450, 480, 510, 540, 570, 600, 630, 660, 690, 720, 750, 780, 810, 840, 870, 900, 930, 960, 990,
    1020, 1050, 1080, 1110, 1140, 1170, 1200, 1230, 1260, 1290, 1320, 1350, 1380, 1410,
  ],
  'E|southbound|e-aurora-85-s': [
    1455, 1495, 1535, 300, 330, 360, 390, 420, 450, 480, 510, 540, 570, 600, 630, 660, 690, 720, 750, 780, 810, 840,
    870, 900, 930, 960, 990, 1020, 1050, 1080, 1110, 1140, 1170, 1200, 1230, 1260, 1290, 1320, 1350, 1380,
  ],
  '44|eastbound|44-ballard': [
    345, 385, 425, 465, 505, 545, 585, 625, 665, 705, 745, 785, 825, 865, 905, 945, 985, 1025, 1065, 1105, 1145, 1185,
    1225, 1265, 1305, 1345, 1385,
  ],
  '44|eastbound|44-fremont': [
    360, 400, 440, 480, 520, 560, 600, 640, 680, 720, 760, 800, 840, 880, 920, 960, 1000, 1040, 1080, 1120, 1160, 1200,
    1240, 1280, 1320, 1360, 1400,
  ],
  '44|eastbound|44-udistrict': [
    375, 415, 455, 495, 535, 575, 615, 655, 695, 735, 775, 815, 855, 895, 935, 975, 1015, 1055, 1095, 1135, 1175, 1215,
    1255, 1295, 1335, 1375, 1415,
  ],
};

export type SchedulesContext = {
  routeId: RouteId | null;
  directionId: DirectionId | null;
  stopId: string | null;
  arrivals: Arrival[];
  checkedAt: string | null;
  error: string | null;
};

function selectedRoute(context: SchedulesContext): Route | undefined {
  return routes.find((route) => route.id === context.routeId);
}

function selectedDirection(context: SchedulesContext) {
  return selectedRoute(context)?.directions.find((direction) => direction.id === context.directionId);
}

function seattleMinutes(date = new Date()): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  }).formatToParts(date);
  const hour = Number(parts.find((part) => part.type === 'hour')?.value ?? 0);
  const minute = Number(parts.find((part) => part.type === 'minute')?.value ?? 0);
  const total = hour * 60 + minute;
  return hour < 3 ? total + 1440 : total;
}

function computeArrivals(context: SchedulesContext): Arrival[] {
  if (!context.routeId || !context.directionId || !context.stopId) return [];
  const times = timetable[`${context.routeId}|${context.directionId}|${context.stopId}`];
  if (!times) return [{ time: '—', minutes: 0, source: 'missing' }];
  const now = seattleMinutes();
  return times
    .filter((minutes) => minutes >= now)
    .slice(0, 3)
    .map((minutes) => ({
      time: `${String(Math.floor((minutes % 1440) / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`,
      minutes: minutes - now,
      source: 'scheduled' as const,
    }));
}

const machineSetup = setup({
  types: {
    context: {} as SchedulesContext,
    events: {} as SchedulesEvent,
  },
  actions: {
    chooseRoute: assign(({ context, event }) =>
      event.type === 'schedule.choose-route'
        ? {
            ...context,
            routeId: event.routeId,
            directionId: null,
            stopId: null,
            arrivals: [],
            checkedAt: null,
            error: null,
          }
        : context,
    ),
    chooseDirection: assign(({ context, event }) =>
      event.type === 'schedule.choose-direction' && isDirectionId(event.directionId)
        ? {
            ...context,
            directionId: event.directionId,
            stopId: null,
            arrivals: [],
            error: null,
          }
        : context,
    ),
    chooseStop: assign(({ context, event }) =>
      event.type === 'schedule.choose-stop' ? { ...context, stopId: event.stopId, arrivals: [], error: null } : context,
    ),
    refreshArrivals: assign(({ context }) => ({
      ...context,
      arrivals: computeArrivals(context),
      checkedAt: new Date().toISOString(),
      error: null,
    })),
  },
  guards: {
    routeSelected: ({ event }) =>
      event.type === 'schedule.choose-route' && routes.some((route) => route.id === event.routeId),
    directionAvailable: ({ context, event }) =>
      event.type === 'schedule.choose-direction' &&
      Boolean(selectedRoute(context)?.directions.some((direction) => direction.id === event.directionId)),
    stopAvailable: ({ context, event }) =>
      event.type === 'schedule.choose-stop' &&
      Boolean(selectedDirection(context)?.stops.some((stop) => stop.id === event.stopId)),
  },
});

export const schedulesMachine = machineSetup.createMachine({
  id: 'schedules',
  initial: 'ready',
  context: { routeId: null, directionId: null, stopId: null, arrivals: [], checkedAt: null, error: null },
  states: {
    ready: {
      on: {
        'schedule.choose-route': { guard: 'routeSelected', actions: 'chooseRoute' },
        'schedule.choose-direction': { guard: 'directionAvailable', actions: 'chooseDirection' },
        'schedule.choose-stop': { guard: 'stopAvailable', actions: ['chooseStop', 'refreshArrivals'] },
        'schedule.refresh-arrivals': { actions: 'refreshArrivals' },
      },
    },
  },
});

export { computeArrivals, selectedDirection, selectedRoute };
